<template>
	<div class="dev-menu">
		<acquisit-button class="dev-icon" uid="dev-menu-trigger" icon="acq/bone" type="icon" :size="26" :icon-size="24"
		                 :color="props.color"
		                 color-no-optimize
		                 ref="$menuButton"
		                 @click="onClick" />
		
		<acquisit-context-menu :items="menuItems"
		                       ref="$menu"
		                       @click="onClickMenuItem"
		                       anchor="top-right" />
		
		<StateInspector ref="$stateInspector" @close="closeInspector" :is-open="isStateInspectorOpen" />
		
		<acquisit-modal ref="$brandingModal" class="branding-debug" type="sheet" title="Branding" show_close_button>
			<div class="colors">
				<div class="color primary">
					<div class="preview" :style="{ background: color.primary.value.cssHSLA, color: color.textPrimaryContrast.value.cssHSLA }">
						<h3>Primary</h3>
					</div>
					
					<acquisit-input label="Hex" :modelValue="debugBranding.primary.hex" @update:modelValue="updateColor('primary', $event)" />
					
					<div class="lines">
						<div class="line hue">
							<label><b>H</b>ue:</label>
							<span>{{ color.primary.value.hsl.h }}</span>
						</div>
						
						<div class="line hue">
							<label><b>S</b>at:</label>
							<span>{{ color.primary.value.hsl.s }}</span>
						</div>
						
						<div class="line hue">
							<label><b>L</b>ight:</label>
							<span>{{ color.primary.value.hsl.l }}</span>
						</div>
						
						<div :class="['line', 'is-red', 'boolean', { true: color.primary.value.isRedish() }]">
							<label>Red?</label>
							<v-svg :file="color.primary.value.isRedish() ? 'acq/checkmark' : 'acq/clear'" />
						</div>
						
						<div :class="['line', 'is-green', 'boolean', { true: color.primary.value.isGreenish() }]">
							<label>Green?</label>
							<v-svg :file="color.primary.value.isGreenish() ? 'acq/checkmark' : 'acq/clear'" />
						</div>
					</div>
				</div>
				
				<div class="color secondary">
					<div class="preview" :style="{ background: color.secondary.value.cssHSLA, color: color.textSecondaryContrast.value.cssHSLA }">
						<h3>Secondary</h3>
					</div>
					
					<acquisit-input label="Hex" :modelValue="debugBranding.secondary.hex" @update:modelValue="updateColor('secondary', $event)" />
					
					<div class="lines">
						<div class="line hue">
							<label><b>H</b>ue:</label>
							<span>{{ color.secondary.value.hsl.h }}</span>
						</div>
						
						<div class="line hue">
							<label><b>S</b>at:</label>
							<span>{{ color.secondary.value.hsl.s }}</span>
						</div>
						
						<div class="line hue">
							<label><b>L</b>ight:</label>
							<span>{{ color.secondary.value.hsl.l }}</span>
						</div>
						
						<div :class="['line', 'is-red', 'boolean', { true: color.secondary.value.isRedish() }]">
							<label>Red?</label>
							<v-svg :file="color.secondary.value.isRedish() ? 'acq/checkmark' : 'acq/clear'" />
						</div>
						
						<div :class="['line', 'is-green', 'boolean', { true: color.secondary.value.isGreenish() }]">
							<label>Green?</label>
							<v-svg :file="color.secondary.value.isGreenish() ? 'acq/checkmark' : 'acq/clear'" />
						</div>
					</div>
				</div>
			</div>
		</acquisit-modal>
		
		<acquisit-modal ref="$versionModal" class="version-debug" type="modal" title="Version" show_close_button>
			<div class="versions">
				<h3>Runtime</h3>
				
				<div class="version">
					<label>Acquisit:</label>
					<span class="value">{{ runningBuild }}</span>
				</div>
				
				<div class="version">
					<label>AcquisitUI:</label>
					<span class="value">{{ uiCurrentVersion }}</span>
				</div>
				
				<div class="version">
					<label>Origin:</label>
					<span class="value">{{ origin }}</span>
				</div>
				
				<div class="version">
					<label>Backend:</label>
					<span class="value">
						{{ backendLabel }}
						<span class="url">{{ backendURL }}</span>
					</span>
				</div>
				
				<h3>Backup</h3>
				
				<div class="version">
					<label>Acquisit:</label>
					<span class="value">{{ build }}</span>
				</div>
				
				<div class="version">
					<label>AcquisitUI:</label>
					<span class="value">{{ ui }}</span>
				</div>
			</div>
		</acquisit-modal>
		
		<Toast ref="screenshotToast" content="Screenshot saved to store" type="info" />
	</div>
</template>

<script setup lang="ts">
	import { computed, nextTick, reactive, ref } from 'vue'
	import { useColor, useColorProps, useEventListener, type Color } from '@broker-as/acquisit-ui-vue/helpers'
	import { type ContextMenuItem, type ContextMenu, type Button, useLogger, VERSION as ACQUISIT_UI_VERSION } from '@broker-as/acquisit-ui-vue/library'
	import { useProtocolStore } from '@/stores/protocol'
	import { storeToRefs } from 'pinia'
	import { useGenericStore } from '@/stores/generic'
	import { useScreenshotsStore } from '@/stores/screenshots'
	import { errorMessage } from '@/lib/ui'
	import { useBackup } from '@/stores/backup'
	import { useBrandingStore } from '@/stores/branding'
	import { usePageStore } from '@/stores/page'
	// @ts-ignore
	import StateInspector from '@ui/dev/StateInspector.vue'
	import Toast from '@ui/core/Toast.vue'
	import type { Modal } from '@broker-as/acquisit-ui-vue/library'
	import { useBackend } from '@/lib/backend-cors'
	import { useLogsStore } from '@/stores/log.ts'
	
	const props = defineProps({
		...useColorProps()
	})
	
	const color = useColor(props)
	const log = useLogger('components/ui/dev/DevMenu', useLogger.COLOR_COMPONENT)
	
	const $stateInspector = ref<typeof StateInspector|null>(),
		$screenshotToast = ref<typeof Toast|null>(),
		$brandingModal = ref<typeof Modal|null>(),
		$versionModal = ref<typeof Modal|null>(),
		$menuButton = ref<typeof Button|null>(),
		$menu = ref<typeof ContextMenu|null>()
	
	const protocolStore = useProtocolStore()
	const { create_pages_pdf: isCreatePagesPDFEnabled } = storeToRefs(protocolStore)
	
	const genericStore = useGenericStore()
	const { backup_enabled: isBackupEnabled, ui, build, origin } = storeToRefs(genericStore)
	const runningBuild = import.meta.env.BUILD
	
	const pageStore = usePageStore()
	const { current_uid: currentPageUID } = storeToRefs(pageStore)
	
	const backend = useBackend()
	
	const uiCurrentVersion = ACQUISIT_UI_VERSION

	const backendLabel = computed(() => {
		return backend.label
	})
	
	const backendURL = computed(() => {
		return backend.baseURL
	})
	
	const debugBranding = reactive({
		primary: {
			hex: color.primary.value.cssHex as string|null|undefined,
		},
		secondary: {
			hex: color.secondary.value.cssHex as string|null|undefined
		}
	})
	
	const isStateInspectorOpen = ref(false)
	
	const menuItems = computed(() => {
		let items: ContextMenuItem[] = [
			{
				id: 'createPagesPDF',
				icon: isCreatePagesPDFEnabled.value ? 'acq/checkmark' : 'acq/cross',
				color: isCreatePagesPDFEnabled.value ? 'green' : 'red',
				label: 'Fullversjon PDF',
			},
			{
				id: 'backup',
				icon: isBackupEnabled.value ? 'acq/checkmark' : 'acq/cross',
				color: isBackupEnabled.value ? 'green' : 'red',
				label: 'Backups',
			},
			{
				divider: true,
			},
			{
				id: 'takeScreenshot',
				shortcut: 'Ctrl+Shift+S',
				label: 'Take screenshot',
				disabled: !isCreatePagesPDFEnabled.value,
			},
			{
				id: 'editBranding',
				label: 'Edit branding…',
			},
			{
				divider: true,
			},
			{
				id: 'exportPDF',
				shortcut: 'Ctrl+P',
				label: 'Export PDF…'
			},
			{
				id: 'downloadScenarios',
				shortcut: 'Ctrl+D',
				label: 'Download scenarios…'
			},
			{
				divider: true,
			},
			{
				divider: true,
				label: 'Inspect'
			},
			{
				id: 'viewVersions',
				shortcut: 'Ctrl+Shift+V',
				label: 'Versions…'
			},
			{
				id: 'viewAPI',
				label: 'API…',
				shortcut: 'Ctrl+Shift+N'
			},
			{
				id: 'viewLogs',
				label: 'Logs…',
				shortcut: 'Ctrl+L'
			},
			{
				id: 'viewSubmission',
				label: 'Submission…',
				shortcut: 'Ctrl+X',
			},
			{
				id: 'viewPage',
				label: 'Current Page…',
				shortcut: 'Ctrl+J'
			}
		]
		
		return items
	})
	
	const onClickMenuItem = (item: ContextMenuItem) => {
		switch (item.id) {
			case 'createPagesPDF':
				protocolStore.setCreatePagesPDF(!isCreatePagesPDFEnabled.value)
				break
			
			case 'backup':
				genericStore.setBackupEnabled(!isBackupEnabled.value)
				break
			
			case 'downloadScenarios':
				openScenarioDownload()
				break
			
			case 'takeScreenshot':
				takeScreenshot()
				break
			
			case 'editBranding':
				openBrandingModal()
				break
			
			case 'viewVersions':
				openVersionModal()
				break
			
			case 'exportPDF':
				exportPDF(false)
				break
			
			case 'viewAPI':
				openAPI()
				break
			
			case 'viewLogs':
				openLogs()
				break
			
			case 'viewSubmission':
				openSubmissionInspector()
				break
			
			case 'viewPage':
				openStateInspector()
				break
		}
	}
	
	const onClick = (e: Event) => {
		e.stopPropagation()
		
		if ($menuButton.value) {
			$menu.value?.openRelativeTo($menuButton.value)
		}
	}
	
	const onKeyUp = (e: KeyboardEvent) => {
		let consumed = false
		
		if (e.ctrlKey) {
			const handlers = {
				'KeyD': () => openScenarioDownload(),
				'KeyI': () => invalidateCurrentPage(),
				'KeyJ': () => openStateInspector(),
				'KeyK': () => openSubmissionInspector(),
				'KeyL': () => openLogs(),
				'KeyN': () => {
					if (e.shiftKey) {
						openAPI()
					}
				},
				'KeyP': () => exportPDF(),
				'KeyB': () => {
					printBackup()
					alert('Backup has been printed to the console')
				},
				'KeyS': () => {
					if (e.shiftKey) {
						takeScreenshot()
						return true
					}
					
					return false
				},
				'KeyV': () => {
					if (e.shiftKey) {
						openVersionModal()
					}
				}
			}
			
			if (handlers[e.code]) {
				const result = handlers[e.code](e)
				
				if (result !== false) {
					consumed = true
				}
			}
		} else if (e.key == 'Escape') {
			closeInspector()
		}
		
		if (consumed) {
			e.preventDefault()
		}
	}
	
	const openStateInspector = async () => {
		isStateInspectorOpen.value = true
		
		await nextTick()
		await $stateInspector.value?.switchToPageDefinition(currentPageUID.value)
	}
	
	const openLogs = async () => {
		isStateInspectorOpen.value = true
		
		await nextTick()
		await $stateInspector.value?.switchToLogs()
	}
	
	const openSubmissionInspector = async () => {
		isStateInspectorOpen.value = true
		
		await nextTick()
		await $stateInspector.value?.switchToSubmission()
	}
	
	const openAPI = async () => {
		isStateInspectorOpen.value = true
		
		await nextTick()
		await $stateInspector.value?.switchToAPI()
	}
	
	const openScenarioDownload = async () => {
		isStateInspectorOpen.value = true
		
		await nextTick()
		await $stateInspector.value?.switchToPDFDebugger()
	}
	
	const closeInspector = async () => {
		isStateInspectorOpen.value = false
	}
	
	const invalidateCurrentPage = () => {
		usePageStore().invalidate()
		log.always.infoFrom('invalidateCurrentPage', 'Invalidated current page using pageStore')
		useLogsStore().info('[DevMenu]', 'Invalidated current page')
	}
	
	const exportPDF = async (force: boolean = false) => {
		// @ts-ignore
		const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1 || typeof InstallTrigger !== 'undefined'
		
		if (!isFirefox && !force) {
			if (confirm('Generating PDFs on the fly only works reliably in Firefox. Do you want to try anyway?')) {
				await exportPDF(true)
			}
			
			return false
		}
		
		log.info('Generating PDF from current screenshots…')
		
		const dataURI = await useScreenshotsStore().createPDF()
		window.open(dataURI as string, '_blank')
	}
	
	const takeScreenshot = async () => {
		if (!isCreatePagesPDFEnabled.value) {
			await errorMessage('Pages PDF is disabled')
		} else if (currentPageUID.value) {
			await useScreenshotsStore().takeForPage(currentPageUID.value)
			
			if ($screenshotToast.value) {
				$screenshotToast.value.open(3000)
			} else {
				alert('Screenshot stored')
			}
		} else {
			await errorMessage('There is no current page set.')
		}
	}
	
	const openBrandingModal = () => {
		$brandingModal.value?.openModal()
	}
	
	const openVersionModal = () => {
		$versionModal.value?.openModal()
	}
	
	const printBackup = () => {
		const backup = useBackup()
		const back_up = backup.create(),
			back_up_json = JSON.stringify(back_up)
		
		log.always.log('BACKUP', back_up)
		log.always.log('BACKUP JSON', back_up_json)
		log.always.info('BACKUP SIZE', back_up_json.length / 1024, 'kb')
	}
	
	const updateColor = (type: string, hex: string) => {
		const c = color.parse(hex)
		const brandingStore = useBrandingStore()
		
		if (type == 'primary') {
			brandingStore.setColors(c, brandingStore.color.secondary as Color)
		} else if (type == 'secondary') {
			brandingStore.setColors(brandingStore.color.primary as Color, c)
		} else {
			throw new Error('Unknown type: ' + type)
		}
	}
	
	useEventListener(document, 'keyup', onKeyUp)
	
	defineExpose({
		openStateInspector,
		openSubmissionInspector,
		closeInspector
	})
</script>

<style lang="scss" scoped>
	@use '@/assets/mixins.scss' as m;
	
	.dev-menu {
		@include m.unselectable;
		position: relative;
		z-index: 999;
		
		.icon {
			position: relative;
			height: 28px;
			width: 28px;
		}
	}
	
	.branding-debug {
		
		:deep(.header) {
			background: none !important;
			color: m.color("text") !important;
		}
		
		:deep(.modal-wrapper) {
			background: transparent;
		}
		
		.colors {
			display: flex;
			align-items: flex-start;
			
			> .color {
				width: calc(50% - 12px);
				
				.preview {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 48px;
					border-radius: 6px;
					margin-bottom: 24px;
					
					box-shadow: inset 0px 0px 0px 1px rgba(#000, 0.14);
				}
				
				& + .color {
					margin-left: 24px;
				}
				
				h3 {
					font-size: 1.2rem;
					font-weight: 600;
				}
				
				.lines {
					margin-top: 16px;
					line-height: 1.5;
					
					.line {
						padding: {
							top: 4px;
							bottom: 4px;
						}
						
						label {
							color: m.color("light-grey-blue");
							display: inline-block;
							min-width: 64px;
							
							b {
								font-weight: 900;
							}
						}
						
						> span {
							font-family: m.font(monospace);
						}
						
						.svg {
							display: inline-block;
							
							width: 20px;
							height: 20px;
							vertical-align: middle;
							margin-top: -6px;
							margin-left: -4px;
						}
						
						&.boolean {
							color: m.color("red");
							
							&.true {
								color: m.color("green");
							}
						}
					}
				}
			}
		}
	}
	
	.version-debug {
		
		.versions {
			display: flex;
			flex-direction: column;
			gap: 12px;
			
			h3 {
				font-size: 1.3rem;
				font-weight: 700;
				text-align: center;
			}
			
			.version {
				display: flex;
				align-items: baseline;
				gap: 8px;
				
				label {
					font-weight: 600;
					width: 45%;
					text-align: right;
				}
				
				.value {
					width: 55%;
					font-family: m.font(monospace);
					
					.url {
						display: block;
						font-family: m.font(main);
						color: m.color("light-grey-blue");
					}
				}
				
				+ h3 {
					margin-top: 24px;
				}
			}
		}
	}
	
	.scale-enter-active,
	.scale-leave-active {
		transition: all 0.2s ease;
	}
	
	.scale-enter-from {
		transform-origin: top;
		transform: scaleY(0.01);
		opacity: 0;
	}
	
	.scale-leave-to {
		transform-origin: top;
		transform: translateY(-42px) scaleY(0.1);
		opacity: 0;
	}
	
	.scale-leave-from,
	.scale-enter-to {
		transform-origin: top;
		transform: scaleY(1);
		opacity: 1;
	}
</style>
